import { Client, Coach, PageProps } from "@/types/page";
import { router } from "@inertiajs/react";
import { atom, getDefaultStore } from "jotai";
import { selectAtom } from "jotai/utils";
import { useEffect, useState } from "react";

const pageDataStr = document
    .querySelector("[data-page]")
    ?.getAttribute("data-page");
const initialPageProps = JSON.parse(pageDataStr ?? "{}").props as PageProps;

export const pagePropsAtom = atom(
    initialPageProps,
    (get, set, props: PageProps) => {
        set(pagePropsAtom, props);
    },
);

/**
 * Use this one if you only want the user id and type. It only updates
 * when the user actually changes, and stays null until they pass two-factor.
 */
export const userIdentityAtom = selectAtom(
    pagePropsAtom,
    (pageProps) =>
        pageProps.user && pageProps.twoFactorPassed
            ? {
                  id: pageProps.user.id,
                  type: pageProps.user.type,
              }
            : null,
    (a, b) => a?.id === b?.id && a?.type === b?.type,
);

router.on("navigate", (event) => {
    getDefaultStore().set(
        pagePropsAtom,
        event.detail.page.props as unknown as PageProps,
    );
});

export function getPageProps<T extends PageProps>() {
    if (window.isTurboPage) {
        return {} as T;
    }
    return getDefaultStore().get(pagePropsAtom) as T;
}

export function usePageProps<T extends PageProps>() {
    const [pageProps, setPageProps] = useState<T>(getPageProps<T>());

    useEffect(() => {
        const cleanup = router.on("navigate", (event) => {
            setPageProps(event.detail.page.props as unknown as T);
        });
        return () => cleanup();
    }, []);

    return pageProps;
}

export default function dataLayerUserData(user: Client | Coach) {
    return {
        user_id: `${user.type}|${user.id}`,
        user_type: user.type,
        user_email: user.email,
        user_phone: user.phone_e164,
        first_name: user.first_name,
        last_name: user.last_name,
        has_payment_setup:
            (user.type === "client" && user.has_payment_setup) ?? false,
        hashed_email: user.hashed_email,
    };
}
